import { getClientEpisodes, mockRelationships } from '../../../data/clientChartApi';
import { ClientGroup, mockClientGroups, mockClients, type Client } from '../../caseload/data';
import type { Episode, EpisodeParty } from '../data/types';

/** One way this contact relates to the client, tied to a specific Episode of Care (or explicit non-episode). */
export interface ClientOverviewContactSegment {
  /** Display role: Partner, Parent, Guarantor, etc. */
  roleLabel: string;
  /** Episode name (e.g. Couples Therapy) or a clear non-EoC label for linked accounts. */
  episodeOfCareLabel: string;
}

export interface ClientOverviewContactRow {
  dedupeKey: string;
  displayName: string;
  /** Comma-separated roles, e.g. Partner, Guarantor */
  rolesSummary: string;
  /** Each role tied to an EoC, e.g. Partner — Couples Therapy, Guarantor — Couples Therapy */
  episodesOfCareSummary: string;
  segments: ClientOverviewContactSegment[];
  /** When set, the contact has a client chart — name links here first. */
  clientChartId?: string;
  /** Billing tab on this client's profile (primary billing member for the representative shared EoC, or self). */
  guarantorBillingProfileClientId?: string;
  /** When set, resolves to Client Profile → Contacts (`getProfileContactsForClient`). */
  profileContactId?: string;
  /** From linked client record, episode party, or linked account when available */
  phone?: string;
  email?: string;
}

const SHARED_GROUP_TYPES: ClientGroup['type'][] = ['Couples Therapy', 'Family Therapy'];

const LINKED_ACCOUNT_EOC_LABEL = 'Outside episodes of care';

/**
 * Group IDs for this client: explicit `client.groups` plus any couples/family group in
 * `mockClientGroups` that lists this client as a member. The second path keeps contacts
 * correct when UI state omits `groups` (optional field) after edits or partial loads.
 */
function resolveSharedTherapyGroupIds(client: Client): string[] {
  const ids = new Set<string>(client.groups ?? []);
  for (const g of mockClientGroups) {
    if (!SHARED_GROUP_TYPES.includes(g.type)) continue;
    if (g.members.some((m) => m.clientId === client.id)) {
      ids.add(g.id);
    }
  }
  return [...ids];
}

function segmentKey(s: ClientOverviewContactSegment): string {
  return `${s.roleLabel}\0${s.episodeOfCareLabel}`;
}

/**
 * When several couples/family EoCs exist, pick the most billing-relevant one:
 * non-finished shared episodes, newest startDate first.
 */
function pickRepresentativeSharedEpisode(episodes: Episode[]): Episode | undefined {
  const candidates = episodes.filter(
    (e) =>
      (e.episodeType === 'couples' || e.episodeType === 'family') &&
      Boolean(e.groupId) &&
      e.status !== 'finished',
  );
  if (candidates.length === 0) return undefined;
  return [...candidates].sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
}

/** Primary billing member for insurance on this EoC (mockRelationships), else the viewing client. */
function resolveBillingProfileClientId(viewingClientId: string, episode: Episode | undefined): string {
  if (!episode?.groupId) return viewingClientId;
  const rel = mockRelationships.find((r) => r.id === episode.groupId);
  return rel?.primaryBillingMemberId ?? viewingClientId;
}

function isKnownClientId(id: string): boolean {
  return mockClients.some((c) => c.id === id);
}

/** Stable merge key: known patient IDs always use client:* so guarantor + shared-group rows collapse. */
function dedupeKeyForParty(party: EpisodeParty): string {
  if (party.id && isKnownClientId(party.id)) {
    return `client:${party.id}`;
  }
  if (party.id?.trim()) {
    return `party:${party.id}`;
  }
  return `name:${party.name.trim().toLowerCase()}`;
}

/**
 * Episode of Care display name for a shared therapy group on this client's chart
 * (matches Medical Record EoC titles, e.g. "Couples Therapy").
 */
function primaryEpisodeNameForGroup(episodes: Episode[], groupId: string): string {
  const matching = episodes.filter(
    (e) =>
      e.groupId === groupId &&
      (e.episodeType === 'couples' || e.episodeType === 'family') &&
      e.name?.trim(),
  );
  if (matching.length === 0) {
    const g = mockClientGroups.find((cg) => cg.id === groupId);
    return g?.type ?? 'Shared therapy';
  }
  const newest = [...matching].sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
  return newest.name.trim();
}

function summarizeRoles(segments: ClientOverviewContactSegment[]): string {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const s of segments) {
    if (seen.has(s.roleLabel)) continue;
    seen.add(s.roleLabel);
    ordered.push(s.roleLabel);
  }
  return ordered.join(', ') || '—';
}

function summarizeEpisodesOfCare(segments: ClientOverviewContactSegment[]): string {
  if (segments.length === 0) return '—';
  return segments.map((s) => `${s.roleLabel} — ${s.episodeOfCareLabel}`).join(', ');
}

interface MutableContact {
  dedupeKey: string;
  displayName: string;
  segments: ClientOverviewContactSegment[];
  clientChartId?: string;
  isGuarantor: boolean;
  profileContactId?: string;
  phone?: string;
  email?: string;
}

function preferNonEmpty(a: string | undefined, b: string | undefined): string | undefined {
  const t = (a ?? '').trim();
  if (t) return t;
  const u = (b ?? '').trim();
  return u || undefined;
}

function mergeSegments(into: MutableContact, more: ClientOverviewContactSegment[]): void {
  const keys = new Set(into.segments.map(segmentKey));
  for (const s of more) {
    const k = segmentKey(s);
    if (keys.has(k)) continue;
    keys.add(k);
    into.segments.push(s);
  }
}

/**
 * Aggregates EoC guarantors and client linked accounts for the Overview tab.
 * Couples/family co-members are intentionally omitted (they belong on the episode, not here).
 * Pass `episodes` from chart state when already loaded to avoid a second bundle resolve.
 */
export function getClientOverviewContacts(client: Client | undefined | null, episodesArg?: Episode[]): ClientOverviewContactRow[] {
  if (!client?.id) {
    return [];
  }
  const episodes = episodesArg ?? getClientEpisodes(client.id);
  const repEp = pickRepresentativeSharedEpisode(episodes);
  const guarantorBillingTarget = resolveBillingProfileClientId(client.id, repEp);

  const pending: MutableContact[] = [];

  const groupIds = resolveSharedTherapyGroupIds(client);
  for (const gid of groupIds) {
    const g = mockClientGroups.find((cg) => cg.id === gid);
    if (!g || !SHARED_GROUP_TYPES.includes(g.type)) continue;

    const eocName = primaryEpisodeNameForGroup(episodes, gid);

    for (const m of g.members) {
      if (m.clientId === client.id) continue;
      const other = mockClients.find((cl) => cl.id === m.clientId);
      const roleLabel = (m.role ?? 'Member').trim() || 'Member';
      pending.push({
        dedupeKey: `client:${m.clientId}`,
        displayName: other?.name ?? m.clientName,
        segments: [{ roleLabel, episodeOfCareLabel: eocName }],
        clientChartId: m.clientId,
        isGuarantor: false,
        phone: other?.phone?.trim() || undefined,
        email: other?.email?.trim() || undefined,
      });
    }
  }

  for (const ep of episodes) {
    const party = ep.guarantor;
    if (!party?.name?.trim()) continue;
    if (party.id === client.id) continue;

    const key = dedupeKeyForParty(party);
    const chartId = party.id && isKnownClientId(party.id) ? party.id : undefined;
    const eocName = ep.name?.trim() || 'Episode of care';
    const guarantorClient = chartId ? mockClients.find((cl) => cl.id === chartId) : undefined;

    pending.push({
      dedupeKey: key,
      displayName: party.name.trim(),
      segments: [{ roleLabel: 'Guarantor', episodeOfCareLabel: eocName }],
      clientChartId: chartId,
      isGuarantor: true,
      phone: preferNonEmpty(guarantorClient?.phone, party.phone),
      email: guarantorClient?.email?.trim() || undefined,
    });
  }

  for (const la of client.linkedAccounts ?? []) {
    if (!la.name?.trim()) continue;
    const roleLabel = (la.role ?? 'Related contact').trim() || 'Related contact';
    const dedupeKey = la.id?.trim()
      ? `linked:${la.id.trim()}`
      : `related:${la.name.trim().toLowerCase()}:${(la.role ?? '').toLowerCase()}`;
    pending.push({
      dedupeKey,
      displayName: la.name.trim(),
      segments: [{ roleLabel, episodeOfCareLabel: LINKED_ACCOUNT_EOC_LABEL }],
      clientChartId: undefined,
      isGuarantor: false,
      profileContactId: la.id?.trim() || undefined,
      phone: la.phone?.trim() || undefined,
      email: la.email?.trim() || undefined,
    });
  }

  const byKey = new Map<string, MutableContact>();
  for (const row of pending) {
    const existing = byKey.get(row.dedupeKey);
    if (!existing) {
      byKey.set(row.dedupeKey, {
        dedupeKey: row.dedupeKey,
        displayName: row.displayName,
        segments: [...row.segments],
        clientChartId: row.clientChartId,
        isGuarantor: row.isGuarantor,
        profileContactId: row.profileContactId,
        phone: row.phone,
        email: row.email,
      });
      continue;
    }
    mergeSegments(existing, row.segments);
    existing.clientChartId = existing.clientChartId ?? row.clientChartId;
    existing.isGuarantor = existing.isGuarantor || row.isGuarantor;
    existing.profileContactId = existing.profileContactId ?? row.profileContactId;
    existing.phone = preferNonEmpty(existing.phone, row.phone);
    existing.email = preferNonEmpty(existing.email, row.email);
  }

  const rows: ClientOverviewContactRow[] = [...byKey.values()].map((m) => ({
    dedupeKey: m.dedupeKey,
    displayName: m.displayName,
    rolesSummary: summarizeRoles(m.segments),
    episodesOfCareSummary: summarizeEpisodesOfCare(m.segments),
    segments: m.segments,
    clientChartId: m.clientChartId,
    guarantorBillingProfileClientId: m.isGuarantor ? guarantorBillingTarget : undefined,
    profileContactId: m.profileContactId,
    phone: m.phone,
    email: m.email,
  }));

  return rows.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export function contactRowHasGuarantorRole(row: ClientOverviewContactRow): boolean {
  return row.segments.some((s) => s.roleLabel === 'Guarantor');
}
